import { useMemo, useState, useEffect, useRef } from "react";

export const usePasteAndDropImageHandler = ({ disabled,selectedImages, setSelectedImages }) => {
    const safeSelectedImages = Array.isArray(selectedImages) ? selectedImages : [];
    const [isDragOver, setIsDragOver] = useState(false);
    const pasteAreaRef = useRef(null);

    const isDuplicate = (file) => {
        return safeSelectedImages.some(existingFile => {
            if (typeof existingFile?.file === "string") return false;
            return (
                existingFile.file.name === file.name &&
                existingFile.file.size === file.size &&
                existingFile.file.lastModified === file.lastModified
            );
        });
    };

    const convertToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const processFiles = async (files) => {
        const maxSizeInBytes = 20 * 1024 * 1024;
        const validFiles = [];

        for (const file of files) {
            if (file.type.startsWith("image/")) {
                if (file.size >= maxSizeInBytes) {
                    alert("File size should not exceed 20MB");
                    continue;
                }
                if (!isDuplicate(file)) {
                    const base64String = await convertToBase64(file);
                    validFiles.push({ file, base64: base64String.split(",")[1] });
                }
            }
        }
        if (validFiles.length) {
            if (Array.isArray(selectedImages)) {
                setSelectedImages([...selectedImages, ...validFiles]);
            } else {
                setSelectedImages([...validFiles]);
            }

        }
    };


    const handlePaste = async (event) => {
        if (disabled) return;
        const items = event.clipboardData?.items;
        if (!items) return;
        event.preventDefault();

        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
                const file = items[i].getAsFile();
                if (file) files.push(file);
            }
        }
        await processFiles(files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        if (event.target === pasteAreaRef.current) {
            setIsDragOver(false);
        }
    };

    const handleDrop = async (event) => {
        if (disabled) return;
        event.preventDefault();
        setIsDragOver(false);

        const files = event.dataTransfer.files;
        if (files && files.length) {
            await processFiles(Array.from(files));
        }
    };

    useEffect(() => {
        const pasteArea = pasteAreaRef.current;
        if (pasteArea) {
            pasteArea.addEventListener("paste", handlePaste);
            pasteArea.addEventListener("dragover", handleDragOver);
            pasteArea.addEventListener("dragleave", handleDragLeave);
            pasteArea.addEventListener("drop", handleDrop);
            return () => {
                pasteArea.removeEventListener("paste", handlePaste);
                pasteArea.removeEventListener("dragover", handleDragOver);
                pasteArea.removeEventListener("dragleave", handleDragLeave);
                pasteArea.removeEventListener("drop", handleDrop);
            };
        }
    }, [disabled, safeSelectedImages, isDragOver]);

    return useMemo(() => ({
        pasteAreaRef,isDragOver
    }), [pasteAreaRef,isDragOver])
}